import 'reflect-metadata';

import Link from 'next/link';
import Router from 'next/router';
import { Component } from 'react';
import InternalMotiveComponent from '../../components/ui/motive/internal-motive.component';
import { MotiveComponent } from '../../components/ui/motive/motive.component';
import { AdSlots } from '../ui/motive/ad-slots';
import { lazyInject } from '../../context/module';
import { ApiConfig } from '../../services/api-config';
import { EnvironmentService } from '../../services/environment.service';
import { LightHttp } from '../../services/light-http.service';
import { MotivesService } from '../../services/motives.service';
import { ScriptLoadService } from '../../services/script-load.service';
import { WebSocketService } from '../../services/websocket.service';
import { WindowService } from '../../services/window.service';
import { BrowserUtils } from '../../ui-utils/browser-utils';
import { MotiveConfig, MotiveLocationTypes } from '../../utils/request.utils';
import { FeatureSearchBox } from './feature-search-box/feature-search-box.component';
import { PageSelectionDropdown } from './feature-select-dropdown/feature-select-dropdown.component';
import styles from './home.component.module.scss';

// types.ts


// Define the interfaces for props and state
interface HomeComponentState {
    selectedFormat: string | null;
    selectedPageURL: string | null;
    statsLoaded: boolean;
    globalStats: {
        workflowsCompleted?: number;
        uploadsCount?: number;
        uploadBytes?: number;
        downloadsCount?: number;
        downloadBytes?: number;
    };
}


export default class HomeComponent extends Component<any, HomeComponentState> {
    showHistory = true;
    siteHistory = '';
    effectiveMotives: MotiveConfig;

    internalAdsEnabled: boolean;
    isDomainSupportOfflineProcessing: boolean;

    @lazyInject(EnvironmentService) envService: EnvironmentService;
    @lazyInject(WindowService) public windowService: WindowService;
    @lazyInject(ScriptLoadService) private scriptLoader: ScriptLoadService;
    @lazyInject(MotivesService) private motivesService: MotivesService;
    @lazyInject(WebSocketService) private webSocketService: WebSocketService;
    @lazyInject(ApiConfig) private apiConfig: ApiConfig;
    @lazyInject(LightHttp) private lightHttp: LightHttp;

    readonly state: Readonly<HomeComponentState> = this.initializeFeatureDataAndGetState();
    constructor(props) {
        super(props);
        this.motivesService.onEffectiveMotiveSettingsChange.subscribe(
            (motives) => {
                this.effectiveMotives = motives;
                this.internalAdsEnabled = this.effectiveMotives && this.effectiveMotives.internalMotivesEnabled;
            },
            (error) => console.error(error),
        );

        if (this.envService.env.showHistory) {
            this.siteHistory = props.history;
        }
    }

    initializeFeatureDataAndGetState(): HomeComponentState {
        if (BrowserUtils.hasWindow()) {
            this.fetchGlobalStats();
        }
        return {
            selectedFormat: '7Z', // Set an initial selected value
            selectedPageURL: null,
            statsLoaded: false,
            globalStats: {},
        };
    }

    private fetchGlobalStats() {
        try {
            this.lightHttp.post<any>(this.apiConfig.getGlobalStatsEndpoint(), {}).subscribe({
                next: (data) => {
                    this.setState({ globalStats: data, statsLoaded: true });
                },
                error: () => {
                    this.setState({ statsLoaded: false });
                },
            });
        } catch (e) {
            // SSR or service not available
        }
    }

    private formatNumber(num: number | undefined): string {
        if (num === undefined || num === null) return '0';
        return num.toLocaleString();
    }

    private formatBytes(bytes: number | undefined): string {
        if (bytes === undefined || bytes === null || bytes === 0) return '0 B';
        const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return (bytes / Math.pow(1024, i)).toFixed(1) + ' ' + units[i];
    }

    navigateToLink(link) {
        if (BrowserUtils.hasWindow()) {
            // const router = useRouter();
            Router.push(link);
        }
    }

    handleFormatChange = (newValue: string | null) => {
        this.setState({ selectedFormat: newValue });
    };

    handleFeatureSelectEvent(selectedPageURL: string | null) {
        this.setState({ selectedPageURL: selectedPageURL });
        Router.push(selectedPageURL);
    }

    render() {
        return <div>
            <div className="gcontent-container">
                <MotiveComponent
                    data={AdSlots.pageTop} containerWidth="100%" containerHeight="90px"
                    location={MotiveLocationTypes.homePageTop}
                    adWidth="970px"
                    adHeight="90px" showAds={true} className='width_major'
                >
                </MotiveComponent>
                <InternalMotiveComponent
                    internalMotiveId="728"
                    containerWidth="100%" containerHeight="90px" adWidth="970px" adHeight="90px" showAds={true}
                ></InternalMotiveComponent>
            </div>
            <section className="py-5 py-md-5">
                <div className="container text-center">
                    <h1 className="display-4 fw-bolder text-dark lh-sm">
                        Free Online <span className={styles['text-gradient']}>File Converter</span> & Compressor
                    </h1>
                    <p className="mt-3 mx-auto lead text-secondary" style={{ maxWidth: '700px' }}>
                        Effortlessly manage, transform, and optimize your digital files. High-quality, secure, and always free. No watermarks, ever.
                    </p>
                    <FeatureSearchBox />
                    <div className={styles.divider}>or browse by category</div>
                    <PageSelectionDropdown
                        options={this.props.selectorOptions}
                        pageUrl={this.state.selectedPageURL}
                        onChange={(v) => this.setState({ selectedPageURL: v })}
                        onSubmit={(v) => this.handleFeatureSelectEvent(v)}>
                    </PageSelectionDropdown>
                    <div className={styles['quick-links']}>
                        <Link href="/audio-compressor" className={styles['quick-link']}>Audio Compressor</Link>
                        <Link href="/compress-mp3" className={styles['quick-link']}>Compress MP3</Link>
                        <Link href="/compress-gif" className={styles['quick-link']}>Compress GIF</Link>
                        <Link href="/compress-mp4" className={styles['quick-link']}>Compress MP4</Link>
                        <Link href="/video-compressor" className={styles['quick-link']}>Video Compressor</Link>
                        <Link href="/image-compressor" className={styles['quick-link']}>Image Compressor</Link>
                        <Link href="/compress-png" className={styles['quick-link']}>Compress PNG</Link>
                        <Link href="/merge-image-to-pdf" className={styles['quick-link']}>Images to PDF</Link>
                        <Link href="/compress-pdf" className={styles['quick-link']}>Compress PDF</Link>
                    </div>
                    <div className={styles['quick-links']}>
                        <Link href="/image-converter" className={styles['quick-link']}>Image Converter</Link>
                        <Link href="/video-converter" className={styles['quick-link']}>Video Converter</Link>
                        <Link href="/audio-converter" className={styles['quick-link']}>Audio Converter</Link>
                        <Link href="/image-resizer" className={styles['quick-link']}>Image Resizer</Link>
                        <Link href="/merge-pdf" className={styles['quick-link']}>Merge PDF</Link>
                        <Link href="/video-trimmer" className={styles['quick-link']}>Video Trimmer</Link>
                        <Link href="/image-to-text" className={styles['quick-link']}>Image to Text</Link>
                        <Link href="/color-converter" className={styles['quick-link']}>Color Converter</Link>
                    </div>
                </div>
            </section>

            <section id="tools" className="py-5 py-md-5 bg-light">
                <div className="container">
                    <div className="text-center">
                        <h2 className="fs-2 fw-bold text-dark">A Complete Suite of Tools</h2>
                        <p className="mt-3 mx-auto lead text-secondary" style={{ maxWidth: '600px' }}>
                            Whatever your task, we have the right tool for the job. Explore our powerful converters and utilities.
                        </p>
                    </div>
                    <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-4 g-4 mt-5">
                        <div className="col">
                            <Link href="/image-tools" className={`card h-100 border-0 rounded-4 shadow-sm p-4 text-center hover-lift-shadow ${styles['hover-lift-shadow']} text-decoration-none`}>
                                <span className="fs-1 mb-3">🖼️</span>
                                <h3 className="fs-5 fw-bold text-dark">Image Tools</h3>
                                <p className="small text-secondary mt-2">Convert, compress, resize, and crop JPG, PNG, HEIC, WebP, and RAW files.</p>
                            </Link>
                        </div>
                        <div className="col">
                            <Link href="/video-tools" className={`card h-100 border-0 rounded-4 shadow-sm p-4 text-center hover-lift-shadow ${styles['hover-lift-shadow']} text-decoration-none`}>
                                <span className="fs-1 mb-3">🎬</span>
                                <h3 className="fs-5 fw-bold text-dark">Video Tools</h3>
                                <p className="small text-secondary mt-2">Convert formats, compress file size, cut clips, and extract audio from MP4, MOV, AVI.</p>
                            </Link>
                        </div>
                        <div className="col">
                            <Link href="/document-tools" className={`card h-100 border-0 rounded-4 shadow-sm p-4 text-center hover-lift-shadow ${styles['hover-lift-shadow']} text-decoration-none`}>
                                <span className="fs-1 mb-3">📄</span>
                                <h3 className="fs-5 fw-bold text-dark">Document Tools</h3>
                                <p className="small text-secondary mt-2">Merge, split, compress, and convert PDFs to Word, JPG, and other formats.</p>
                            </Link>
                        </div>
                        <div className="col">
                            <Link href="/unit-converter" className={`card h-100 border-0 rounded-4 shadow-sm p-4 text-center hover-lift-shadow ${styles['hover-lift-shadow']} text-decoration-none`}>
                                <span className="fs-1 mb-3">📏</span>
                                <h3 className="fs-5 fw-bold text-dark">Unit Converters</h3>
                                <p className="small text-secondary mt-2">Quickly convert length, weight, volume, temperature, data size, and more.</p>
                            </Link>
                        </div>
                        <div className="col">
                            <Link href="/time-converter" className={`card h-100 border-0 rounded-4 shadow-sm p-4 text-center hover-lift-shadow ${styles['hover-lift-shadow']} text-decoration-none`}>
                                <span className="fs-1 mb-3">📏</span>
                                <h3 className="fs-5 fw-bold text-dark">Time Converters</h3>
                                <p className="small text-secondary mt-2">Quickly convert time between time zones, cities, countries, and more.</p>
                            </Link>
                        </div>
                        <div className="col">
                            <Link href="/audio-tools" className={`card h-100 border-0 rounded-4 shadow-sm p-4 text-center hover-lift-shadow ${styles['hover-lift-shadow']} text-decoration-none`}>
                                <span className="fs-1 mb-3">🎧</span>
                                <h3 className="fs-5 fw-bold text-dark">Audio Tools</h3>
                                <p className="small text-secondary mt-2">Convert between audio formats like MP3, WAV, FLAC, and cut audio tracks.</p>
                            </Link>
                        </div>
                        <div className="col">
                            <Link href="/archive-tools" className={`card h-100 border-0 rounded-4 shadow-sm p-4 text-center hover-lift-shadow ${styles['hover-lift-shadow']} text-decoration-none`}>
                                <span className="fs-1 mb-3">🗄️</span>
                                <h3 className="fs-5 fw-bold text-dark">Archive Tools</h3>
                                <p className="small text-secondary mt-2">Compress and decompress files (Zip, RAR, 7z) for easy storage and sharing.</p>
                            </Link>
                        </div>
                        <div className="col">
                            <Link href="/other-tools" className={`card h-100 border-0 rounded-4 shadow-sm p-4 text-center hover-lift-shadow ${styles['hover-lift-shadow']} text-decoration-none`}>
                                <span className="fs-1 mb-3">🛠️</span>
                                <h3 className="fs-5 fw-bold text-dark">Other Utilities</h3>
                                <p className="small text-secondary mt-2">Tools for encoding/decoding Base64, URL encoding, and quick word counting.</p>
                            </Link>
                        </div>
                    </div>
                    <div className="text-center mt-5">
                        <Link href="/tools" className="text-xconvert-blue-dark fw-semibold text-decoration-none hover-underline">Explore all 5000+ tools &rarr;</Link>
                    </div>
                </div>
            </section>

            <section id="why-us" className="py-5 py-md-5">
                <div className="container">
                    <div className="text-center">
                        <h2 className="fs-2 fw-bold text-dark">Designed for Productivity</h2>
                        <p className="mt-3 mx-auto lead text-secondary" style={{ maxWidth: '600px' }}>
                            We focus on the features that matter to deliver a superior experience.
                        </p>
                    </div>
                    <div className="row row-cols-1 row-cols-md-3 g-4 mt-5">
                        <div className="col">
                            <div className="text-center p-4">
                                <span className="fs-1 text-xconvert-blue-dark">🏆</span>
                                <h3 className="fs-5 fw-bold text-dark mt-3">Uncompromising Quality</h3>
                                <p className="small text-secondary mt-2">Advanced conversion engines preserve the quality of your original file, with customizable settings for full control.</p>
                            </div>
                        </div>
                        <div className="col">
                            <div className="text-center p-4">
                                <span className="fs-1 text-xconvert-blue-dark">⚡️</span>
                                <h3 className="fs-5 fw-bold text-dark mt-3">Blazing-Fast Speed</h3>
                                <p className="small text-secondary mt-2">Powered by a robust cloud infrastructure, your files are processed in seconds. Batch processing saves even more time.</p>
                            </div>
                        </div>
                        <div className="col">
                            <div className="text-center p-4">
                                <span className="fs-1 text-xconvert-blue-dark">🛡️</span>
                                <h3 className="fs-5 fw-bold text-dark mt-3">Free & Secure</h3>
                                <p className="small text-secondary mt-2">Enjoy our full suite of tools for free, without watermarks. Your files are encrypted and automatically deleted.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="security" className="py-5 py-md-5 bg-dark text-white">
                <div className="container text-center">
                    <h2 className="fs-2 fw-bold">Trusted by Millions Worldwide</h2>
                    {this.state.statsLoaded ? (
                        <div className="row row-cols-1 row-cols-md-3 g-4 mt-4">
                            <div className="col">
                                <div className="fs-1 fw-bolder">{this.formatNumber(this.state.globalStats.workflowsCompleted)}</div>
                                <p className="text-light mt-1">Files Processed</p>
                            </div>
                            <div className="col">
                                <div className="fs-1 fw-bolder">{this.formatNumber(this.state.globalStats.uploadsCount)}</div>
                                <p className="text-light mt-1">Files Uploaded ({this.formatBytes(this.state.globalStats.uploadBytes)})</p>
                            </div>
                            <div className="col">
                                <div className="fs-1 fw-bolder">{this.formatNumber(this.state.globalStats.downloadsCount)}</div>
                                <p className="text-light mt-1">Files Downloaded ({this.formatBytes(this.state.globalStats.downloadBytes)})</p>
                            </div>
                        </div>
                    ) : (
                        <div className="row row-cols-1 row-cols-md-3 g-4 mt-4">
                            {[0, 1, 2].map(i => (
                                <div className="col" key={i}>
                                    <div className="placeholder-glow">
                                        <span className="placeholder col-6 fs-1 rounded" style={{ height: '48px', display: 'inline-block' }}></span>
                                    </div>
                                    <p className="text-light mt-1 placeholder-glow"><span className="placeholder col-4 rounded"></span></p>
                                </div>
                            ))}
                        </div>
                    )}
                    {this.state.statsLoaded && <p className="text-secondary small mt-2 mb-0">Stats are updated every minute</p>}
                    <div className="mt-5 mx-auto border-top border-secondary pt-4" style={{ maxWidth: '600px' }}>
                        <p className="lead text-light">
                            <span className="fw-bold">Your privacy is our priority.</span> All files are transferred over a secure SSL connection. We automatically delete all uploaded and converted files from our servers within a few hours.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    }
}
